import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';

import { ArrowBackIosRounded, ArrowForwardIosRounded } from '@mui/icons-material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { BoxProps, Grid, IconButton, Stack, Typography } from '@mui/material';
import { debounce } from '@npi/buddy-utils';

import OffersCard from 'src/pages/AddPartnerPage/components/PricingInformation/OffersCard/OffersCard';

import { Flex } from '../Flex';
import { OptionTypes } from '../FormikComponents/FieldInput';

interface AssignToggle {
    heading?: string;
    onView?: (id: number | string) => void;
    assigned: OptionTypes[];
    unassigned: OptionTypes[];
    onAssign: (options: OptionTypes[]) => void;
    onUnassign: (id: OptionTypes[]) => void;
    assignedTitled: string;
    unassignedTitled: string;
    onClose?: () => void;
    containerProps?: BoxProps;
}

interface AssignBox {
    options: OptionTypes[];
    onView?: (id: number | string) => void;
    onSelectChange: (selectedValues: OptionTypes[]) => void;
    selected: OptionTypes[];
    setSelected: Dispatch<SetStateAction<OptionTypes[]>>;
}

const AssignBox: FC<AssignBox> = ({ options, onView, onSelectChange, selected, setSelected }) => {
    useEffect(() => {
        const debouncedOnChange = debounce(() => {
            return onSelectChange(selected);
        }, 500);
        debouncedOnChange();

        return () => {
            debouncedOnChange.cancel();
        };
    }, [selected, onSelectChange]);

    const onSelect = (selectedOption: OptionTypes) => {
        setSelected((prevState) => {
            if (prevState.includes(selectedOption)) {
                return prevState.filter((el) => {
                    return el !== selectedOption;
                });
            }
            return [...prevState, selectedOption];
        });
    };

    return (
        <Flex
            flexDirection="column"
            gap="8px"
            overflowY="auto"
            overflowX="hidden"
            maxHeight="274px"
            padding="24px"
            paddingRight="8px"
            flex={1}
        >
            {options.map((el) => {
                return (
                    <Flex
                        key={`${el.value}-${el.label}`}
                        justifyContent="space-between"
                        alignItems="center"
                        color={selected.includes(el) ? '#000' : '#9e9e9e'}
                    >
                        <Typography
                            fontSize={18}
                            sx={{ cursor: 'pointer' }}
                            onClick={() => {
                                return onSelect(el);
                            }}
                        >
                            {el.label}
                        </Typography>
                        {onView && (
                            <IconButton
                                data-testid="view-product-preview-button"
                                size="small"
                                onClick={() => {
                                    return onView(el.value);
                                }}
                            >
                                <VisibilityOutlinedIcon
                                    sx={{
                                        color: selected.includes(el) ? '#000' : '#666666',
                                    }}
                                />
                            </IconButton>
                        )}
                    </Flex>
                );
            })}
        </Flex>
    );
};

export const AssignToggle: FC<AssignToggle> = ({
    onView,
    assigned,
    unassigned,
    onAssign,
    onUnassign,
    assignedTitled,
    unassignedTitled,
}) => {
    const [selectedToAssign, setSelectedToAssign] = useState<OptionTypes[]>([]);
    const [selectedToUnassign, setSelectedToUnassign] = useState<OptionTypes[]>([]);

    return (
        <Grid container spacing={3}>
            <Grid item xs={12} md={5.5}>
                <OffersCard title={unassignedTitled}>
                    <AssignBox
                        options={unassigned}
                        onView={onView}
                        onSelectChange={setSelectedToAssign}
                        selected={selectedToAssign}
                        setSelected={setSelectedToAssign}
                    />
                </OffersCard>
            </Grid>

            <Grid item xs={12} md={1} display="flex" alignItems="center" justifyContent="center">
                <Stack direction="column" justifyContent="center" alignItems="center" gap={10}>
                    <IconButton
                        sx={{
                            ...(selectedToUnassign.length > 0 && {
                                color: '#fff',
                                backgroundColor: '#000',
                                '&:hover': {
                                    backgroundColor: '#757575',
                                },
                                borderRadius: '50%',
                            }),
                        }}
                        onClick={() => {
                            onUnassign(selectedToUnassign);
                            setSelectedToUnassign([]);
                        }}
                        disabled={selectedToUnassign.length === 0}
                    >
                        <ArrowBackIosRounded />
                    </IconButton>
                    <IconButton
                        sx={{
                            ...(selectedToAssign.length > 0 && {
                                color: '#fff',
                                backgroundColor: '#000',
                                '&:hover': {
                                    backgroundColor: '#757575',
                                },
                                borderRadius: '50%',
                            }),
                        }}
                        onClick={() => {
                            onAssign(selectedToAssign);
                            setSelectedToAssign([]);
                        }}
                        disabled={selectedToAssign.length === 0}
                    >
                        <ArrowForwardIosRounded />
                    </IconButton>
                </Stack>
            </Grid>

            <Grid item xs={12} md={5.5}>
                <OffersCard title={assignedTitled}>
                    <AssignBox
                        options={assigned}
                        onSelectChange={setSelectedToUnassign}
                        selected={selectedToUnassign}
                        setSelected={setSelectedToUnassign}
                    />
                </OffersCard>
            </Grid>
        </Grid>
    );
};
